import React from "react";
import { FormSectionElements } from "src/entities/formTemplateDataType";
import { LiveValidationErrMsg } from "../live-validation-errMsg/LiveValidationErrMsg";
import { getRootComponent, triggerSummaryListFilter, updateFilterSelectionCode } from "../../utilities/ElementUtils";
import { spacing } from '../../theme';
import { isNullOrEmpty } from "../../utilities/DataUtils";
import classNames from 'classnames';
import FormValidationUtils from "../../utilities/FormValidationUtils";

interface Props {
    field: FormSectionElements;
    editable: boolean;
    title;
    labelControlArray;
    requiredField;
    callbackElement;
    isEng: boolean;
    hint?: string;
    useAsterisks?: string;
    HideOptionalText?: string;
}

interface DateRangeValue {
    startDate: string | null;
    endDate: string | null;
}

export class OdsDateRangeComponent extends React.Component<Props> {
    startYear: string = '';
    startMonth: string = '';
    startDay: string = '';
    endYear: string = '';
    endMonth: string = '';
    endDay: string = '';

    private lastSyncedValue: string | null | undefined = undefined;

    constructor(props: Props) {
        super(props);
        this.syncFromField(this.props.field.Value);
    }

    buildDateRangeValue = (): string | null => {
        const startDate = this.buildSingleDate(this.startMonth, this.startDay, this.startYear);
        const endDate = this.buildSingleDate(this.endMonth, this.endDay, this.endYear);

        const hasStartDate = !isNullOrEmpty(startDate);
        const hasEndDate = !isNullOrEmpty(endDate);

        if (!hasStartDate && !hasEndDate) {
            return null;
        }
        return `${startDate || ''}|${endDate || ''}`;
    }

    buildSingleDate = (month: string, day: string, year: string): string | null => {
        if (month && day && year) {
            return `${month}/${day}/${year}`;
        }
        return null;
    }

    validateDateRange = (): { valid: boolean; errorMessage: string } => {
        const startDate = this.buildSingleDate(this.startMonth, this.startDay, this.startYear);
        const endDate = this.buildSingleDate(this.endMonth, this.endDay, this.endYear);

        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            if (start > end) {
                return {
                    valid: false,
                    errorMessage: this.props.isEng
                        ? 'Start date cannot be after end date'
                        : 'La date de début ne peut pas être postérieure à la date de fin'
                };
            }
        }

        return { valid: true, errorMessage: '' };
    }

    hasPartialDate = (month: string, day: string, year: string): boolean => {
        const hasYear = !isNullOrEmpty(year);
        const hasMonth = !isNullOrEmpty(month);
        const hasDay = !isNullOrEmpty(day);
        const hasAnyValue = hasYear || hasMonth || hasDay;
        const hasAllValues = hasYear && hasMonth && hasDay;
        return hasAnyValue && !hasAllValues;
    }

    isValidDate = (dateString: string | null): boolean => {
        if (!dateString) return true;
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime());
    }

    onDateChange = (e, dateFormat: string, isStartDate: boolean) => {
        let field = this.props.field;
        if (field.ReadOnly) {
            e?.preventDefault?.();
            return;
        }

        let val = e.target.value.trim();
        if (isStartDate) {
            switch (dateFormat) {
                case 'dd': this.startDay = val; break;
                case 'mm': this.startMonth = val; break;
                case 'yyyy': this.startYear = val; break;
            }
        } else {
            switch (dateFormat) {
                case 'dd': this.endDay = val; break;
                case 'mm': this.endMonth = val; break;
                case 'yyyy': this.endYear = val; break;
            }
        }

        const hasPartialStart = this.hasPartialDate(this.startMonth, this.startDay, this.startYear);
        const hasPartialEnd = this.hasPartialDate(this.endMonth, this.endDay, this.endYear);
        const hasAnyValue = !isNullOrEmpty(this.startYear) || !isNullOrEmpty(this.startMonth) ||
            !isNullOrEmpty(this.startDay) || !isNullOrEmpty(this.endYear) ||
            !isNullOrEmpty(this.endMonth) || !isNullOrEmpty(this.endDay);

        if (hasAnyValue) {
            field.Touched = true;
        }

        const finalValue = this.buildDateRangeValue();
        if (hasPartialStart || hasPartialEnd) {
            field.Value = finalValue;
            field.Valid = false;
            field.IsEmpty = false;
            field.ErrorMessage = this.props.isEng
                ? 'Invalid date (please provide full dates)'
                : 'Date invalide (veuillez fournir les dates complètes)';
            field.ErrorMessageFRA = 'Date invalide (veuillez fournir les dates complètes)';
        } else {
            const startDate = this.buildSingleDate(this.startMonth, this.startDay, this.startYear);
            const endDate = this.buildSingleDate(this.endMonth, this.endDay, this.endYear);
            const startValid = !startDate || this.isValidDate(startDate);
            const endValid = !endDate || this.isValidDate(endDate);

            if (!startValid || !endValid) {
                field.Value = finalValue;
                field.Valid = false;
                field.IsEmpty = false;
                field.ErrorMessage = this.props.isEng
                    ? 'Invalid date (please enter valid dates)'
                    : 'Date invalide (veuillez entrer des dates valides)';
                field.ErrorMessageFRA = 'Date invalide (veuillez entrer des dates valides)';
            } else {
                const rangeValidation = this.validateDateRange();
                field.Value = finalValue;
                field.Valid = rangeValidation.valid;
                field.IsEmpty = !finalValue;
                field.ErrorMessage = rangeValidation.errorMessage;
                field.ErrorMessageFRA = rangeValidation.errorMessage;
            }
        }

        this.lastSyncedValue = field.Value;
        FormValidationUtils.runEventJavascript(field);
        this.updateAndTriggerFilter();
        this.props.callbackElement(getRootComponent(this.props.field));
    }

    onBlur = (e) => {
        let field = this.props.field;
        const hasPartialStart = this.hasPartialDate(this.startMonth, this.startDay, this.startYear);
        const hasPartialEnd = this.hasPartialDate(this.endMonth, this.endDay, this.endYear);
        if (hasPartialStart || hasPartialEnd) {
            field.ErrorMessage = this.props.isEng
                ? 'Invalid date (please provide full dates)'
                : 'Date invalide (veuillez fournir les dates complètes)';
            field.ErrorMessageFRA = 'Date invalide (veuillez fournir les dates complètes)';
        } else {
            const rangeValidation = this.validateDateRange();
            field.ErrorMessage = rangeValidation.errorMessage;
            field.ErrorMessageFRA = rangeValidation.errorMessage;
            field.Valid = rangeValidation.valid;
        }

        this.props.callbackElement(getRootComponent(this.props.field));
    }

    updateAndTriggerFilter = () => {
        const startDate = this.buildSingleDate(this.startMonth, this.startDay, this.startYear);
        const endDate = this.buildSingleDate(this.endMonth, this.endDay, this.endYear);
        const filterValue = startDate || endDate ? `${startDate || ''}|${endDate || ''}` : null;
        
        updateFilterSelectionCode(this.props.field, filterValue);
        triggerSummaryListFilter(this.props.field);
    }

    handleSearch = () => {
        const fieldAny = this.props.field as any;
        if (!fieldAny.searchTargetSummaryListId) return;

        // Find the target PagedSummaryList and trigger refresh
        const root = getRootComponent(this.props.field);
        const sections = root?.ListOfFormSections ?? [];

        const findAndRefresh = (elements: any[]): boolean => {
            for (const element of elements ?? []) {
                if (element.Id === fieldAny.searchTargetSummaryListId && element._refreshData) {
                    element._refreshData();
                    return true;
                }
                if (element.subSectionList?.length) {
                    for (const sub of element.subSectionList) {
                        if (findAndRefresh(sub?.ListOfFormSectionElements)) return true;
                    }
                }
            }
            return false;
        };

        for (const section of sections) {
            for (const subSection of section.ListOfFormSubSections ?? []) {
                if (findAndRefresh(subSection.ListOfFormSectionElements)) return;
            }
        }
    }

    render() {
        let field = this.props.field;
        const sectionReadOnly = field.parent?.parent?.SectionReadonly === true;
        field.ReadOnly = field.ReadOnly || sectionReadOnly;

        const inputReadOnly = field.ReadOnly;

        if (field.Value !== this.lastSyncedValue) {
            this.syncFromField(field.Value);
        }

        if (!field.Visible) return null;

        let editable = this.props.editable;
        let controlArray = [];
        let title = this.props.title;

        let style: React.CSSProperties | undefined;
        if (field.MarginBottom) {
            const isNegativeMargin = field.MarginBottom.startsWith("-");
            const spacingToken = isNegativeMargin ? field.MarginBottom.substring(1) : field.MarginBottom;
            const spacingKey = spacingToken as keyof typeof spacing;
            const spacingValue = spacing[spacingKey];
            const marginBottomValue = spacingValue
                ? `${isNegativeMargin ? "-" : ""}${spacingValue}`
                : field.MarginBottom;
            style = { marginBottom: marginBottomValue };
        }

        if (editable) {
            const startYearValue = this.startYear ?? '';
            const startMonthValue = this.startMonth ?? '';
            const startDayValue = this.startDay ?? '';
            const endYearValue = this.endYear ?? '';
            const endMonthValue = this.endMonth ?? '';
            const endDayValue = this.endDay ?? '';

            controlArray.push(
                <div style={{ display: 'block', width: '100%' }} key={`daterange-wrapper-${field.Id}`}>
                <fieldset
                    key={`daterange-${field.Id}`}
                    role="group"
                    className="ontario-fieldset ontario-margin-top-24-!"
                    style={{...style, display: 'block', width: '100%'}}
                    aria-describedby={field.Valid ? undefined : `${field.LoadBindName}-${field.Id}-error`}
                >
                    <legend className="ontario-fieldset__legend ontario-margin-bottom-0-!" style={field?.fieldSize ? { fontSize: field?.fieldSize } : undefined}>
                        {field.Optional !== "Y" && (
                            <>
                                {title}
                                <span className="ontario-label__flag">
                                    {this.props.useAsterisks == "Y" && <span style={{ color: 'red' }}>*</span>}
                                    {this.props.useAsterisks !== "Y" && (
                                        <span className='ontario-label__flag'>
                                            {this.props.isEng ? "(required)" : "(obligatoire)"}
                                        </span>
                                    )}
                                </span>
                            </>
                        )}
                        {field.Optional === "Y" && (
                            <>
                                {title}
                                <span className="ontario-label__flag">
                                    {this.props.HideOptionalText != "Y" && (this.props.isEng ? "(optional)" : "(facultatif)")}
                                </span>
                            </>
                        )}
                    </legend>
                    {this.props.hint && <p className="ontario-hint ontario-margin-top-16-!">{this.props.hint}</p>}
                    <LiveValidationErrMsg
                        field={this.props.field}
                        isEng={this.props.isEng}
                        key={"LiveValidationErrMsg_" + field.LoadBindName}
                        id={`${field.LoadBindName}-${field.Id}`}
                    />
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                            <div className="ontario-margin-bottom-8-!" style={{ fontWeight: 'bold' }}>
                                {this.props.isEng ? 'Start Date' : 'Date de début'}
                            </div>
                            <div className="ontario-date__group">
                                <div className="ontario-date__group-input">
                                    <label htmlFor={`start-year-${field.Id}`}>
                                        {this.props.isEng ? "Year" : "Année"}
                                        <span className="ontario-show-for-sr">(4 digits)</span>
                                    </label>
                                    <input
                                        className={classNames(
                                            "ontario-input",
                                            "ontario-input--4-char-width",
                                            { "ontario-input__error": field.Touched && !field.Valid }
                                        )}
                                        id={`start-year-${field.Id}`}
                                        name={`${field.LoadBindName}-start`}
                                        type="text"
                                        maxLength={4}
                                        value={startYearValue}
                                        readOnly={inputReadOnly}
                                        aria-required={field.Optional !== "Y"}
                                        aria-invalid={!field.Valid ? true : undefined}
                                        onBlur={this.onBlur}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            this.onDateChange(e, "yyyy", true);
                                        }}
                                    />
                                </div>
                                <div className="ontario-date__group-input">
                                    <label htmlFor={`start-month-${field.Id}`}>
                                        {this.props.isEng ? "Month" : "Mois"}
                                        <span className="ontario-show-for-sr">(1 or 2 digits)</span>
                                    </label>
                                    <input
                                        className={classNames(
                                            "ontario-input",
                                            "ontario-input--2-char-width",
                                            { "ontario-input__error": field.Touched && !field.Valid }
                                        )}
                                        id={`start-month-${field.Id}`}
                                        type="text"
                                        maxLength={2}
                                        value={startMonthValue}
                                        readOnly={inputReadOnly}
                                        aria-required={field.Optional !== "Y"}
                                        aria-invalid={!field.Valid ? true : undefined}
                                        onBlur={this.onBlur}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            this.onDateChange(e, "mm", true);
                                        }}
                                    />
                                </div>
                                <div className="ontario-date__group-input">
                                    <label htmlFor={`start-day-${field.Id}`}>
                                        {this.props.isEng ? "Day" : "Jour"}
                                        <span className="ontario-show-for-sr">(1 or 2 digits)</span>
                                    </label>
                                    <input
                                        className={classNames(
                                            "ontario-input",
                                            "ontario-input--2-char-width",
                                            { "ontario-input__error": field.Touched && !field.Valid }
                                        )}
                                        id={`start-day-${field.Id}`}
                                        type="text"
                                        maxLength={2}
                                        value={startDayValue}
                                        readOnly={inputReadOnly}
                                        aria-required={field.Optional !== "Y"}
                                        aria-invalid={!field.Valid ? true : undefined}
                                        onBlur={this.onBlur}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            this.onDateChange(e, "dd", true);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                            <div className="ontario-margin-bottom-8-!" style={{ fontWeight: 'bold' }}>
                                {this.props.isEng ? 'End Date' : 'Date de fin'}
                            </div>
                            <div className="ontario-date__group">
                                <div className="ontario-date__group-input">
                                    <label htmlFor={`end-year-${field.Id}`}>
                                        {this.props.isEng ? "Year" : "Année"}
                                        <span className="ontario-show-for-sr">(4 digits)</span>
                                    </label>
                                    <input
                                        className={classNames(
                                            "ontario-input",
                                            "ontario-input--4-char-width",
                                            { "ontario-input__error": field.Touched && !field.Valid }
                                        )}
                                        id={`end-year-${field.Id}`}
                                        name={`${field.LoadBindName}-end`}
                                        type="text"
                                        maxLength={4}
                                        value={endYearValue}
                                        readOnly={inputReadOnly}
                                        aria-required={field.Optional !== "Y"}
                                        aria-invalid={!field.Valid ? true : undefined}
                                        onBlur={this.onBlur}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            this.onDateChange(e, "yyyy", false);
                                        }}
                                    />
                                </div>
                                <div className="ontario-date__group-input">
                                    <label htmlFor={`end-month-${field.Id}`}>
                                        {this.props.isEng ? "Month" : "Mois"}
                                        <span className="ontario-show-for-sr">(1 or 2 digits)</span>
                                    </label>
                                    <input
                                        className={classNames(
                                            "ontario-input",
                                            "ontario-input--2-char-width",
                                            { "ontario-input__error": field.Touched && !field.Valid }
                                        )}
                                        id={`end-month-${field.Id}`}
                                        type="text"
                                        maxLength={2}
                                        value={endMonthValue}
                                        readOnly={inputReadOnly}
                                        aria-required={field.Optional !== "Y"}
                                        aria-invalid={!field.Valid ? true : undefined}
                                        onBlur={this.onBlur}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            this.onDateChange(e, "mm", false);
                                        }}
                                    />
                                </div>
                                <div className="ontario-date__group-input">
                                    <label htmlFor={`end-day-${field.Id}`}>
                                        {this.props.isEng ? "Day" : "Jour"}
                                        <span className="ontario-show-for-sr">(1 or 2 digits)</span>
                                    </label>
                                    <input
                                        className={classNames(
                                            "ontario-input",
                                            "ontario-input--2-char-width",
                                            { "ontario-input__error": field.Touched && !field.Valid }
                                        )}
                                        id={`end-day-${field.Id}`}
                                        type="text"
                                        maxLength={2}
                                        value={endDayValue}
                                        readOnly={inputReadOnly}
                                        aria-required={field.Optional !== "Y"}
                                        aria-invalid={!field.Valid ? true : undefined}
                                        onBlur={this.onBlur}
                                        onChange={(e) => {
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            this.onDateChange(e, "dd", false);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {(field as any).enableSearch && (field as any).searchTargetSummaryListId && (
                        <div style={{ marginTop: '1rem' }}>
                            <button
                                type="button"
                                className="ontario-button ontario-button--primary"
                                onClick={this.handleSearch}
                            >
                                {this.props.isEng ? 'Search' : 'Rechercher'}
                            </button>
                        </div>
                    )}
                </fieldset>
                </div>
            );
        } else {
            const startDate = this.buildSingleDate(this.startMonth, this.startDay, this.startYear);
            const endDate = this.buildSingleDate(this.endMonth, this.endDay, this.endYear);
            const displayValue = startDate || endDate
                ? `${startDate || 'N/A'} - ${endDate || 'N/A'}`
                : null;

            controlArray.push(
                <div key={`daterange-readonly-${field.Id}`} className='pdf-two-column'>
                    <div>{title}</div>
                    <div className={classNames({ 'invalid-field--highlighted': field.Valid === false })}>
                        {displayValue}
                    </div>
                </div>
            );
        }

        return controlArray;
    }

    private syncFromField(value: unknown) {
        if (typeof value === 'string' && !isNullOrEmpty(value)) {
            const parts = value.split('|');
            const startDateStr = parts[0] || '';
            const endDateStr = parts[1] || '';
            if (startDateStr) {
                const startParts = startDateStr.split('/');
                if (startParts.length === 3) {
                    this.startMonth = startParts[0];
                    this.startDay = startParts[1];
                    this.startYear = startParts[2];
                } else {
                    this.startMonth = '';
                    this.startDay = '';
                    this.startYear = '';
                }
            } else {
                this.startMonth = '';
                this.startDay = '';
                this.startYear = '';
            }
            if (endDateStr) {
                const endParts = endDateStr.split('/');
                if (endParts.length === 3) {
                    this.endMonth = endParts[0];
                    this.endDay = endParts[1];
                    this.endYear = endParts[2];
                } else {
                    this.endMonth = '';
                    this.endDay = '';
                    this.endYear = '';
                }
            } else {
                this.endMonth = '';
                this.endDay = '';
                this.endYear = '';
            }

            this.lastSyncedValue = value;
        } else {
            this.startMonth = '';
            this.startDay = '';
            this.startYear = '';
            this.endMonth = '';
            this.endDay = '';
            this.endYear = '';
            this.lastSyncedValue = value as string | null | undefined;
        }
    }
}
